import React, { useEffect, useState } from 'react';
import { useInsights } from '../features/insights/hooks/useInsights';
import InsightsLead from '../features/insights/components/InsightsLead';
import StrengthsCard from '../features/insights/components/StrengthsCard';
import QuizTrendCard from '../features/insights/components/QuizTrendCard';
import StudyActivityCard from '../features/insights/components/StudyActivityCard';
import BadgesCard from '../features/insights/components/BadgesCard';
import LearningStyleCard from '../features/insights/components/LearningStyleCard';
import { ReviewQueueSection } from '../features/adaptive/components/ReviewQueueSection';
import SEO, { SEO_CONFIG } from '../components/SEO';
import PageHeader, { PageContainer } from '../components/PageHeader';
import TabStrip from '../features/portfolio/components/TabStrip';
import { Skeleton, SkeletonText, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import '../styles/parentPortal.css';
import '../styles/insights.css';

const INSIGHTS_TABS = [
  { to: '/insights', label: 'Analytics' },
  { to: '/insights/portfolio', label: 'Transcript' },
];

/**
 * Analytics screen — the takeaway in a sentence first, the numbers that back it
 * underneath, then the sections that explain the trend.
 */
export default function InsightsPage() {
  const { data, isLoading, error, refetch } = useInsights();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // The header and tab strip need no data, so they render for real — only
  // the charts below are stood in for. This page has the worst layout shift
  // in the app otherwise: a centred spinner collapsing into a dense chart
  // dashboard whose entry animations then fire on top of the jump.
  if (isLoading) {
    return (
      <>
        <SEO {...SEO_CONFIG.insights} />
        <PageContainer>
          <PageHeader
            title="Your learning"
            subtitle="Habits, strengths, and the record your work has built so far."
          />
          <TabStrip tabs={INSIGHTS_TABS} />

          <SkeletonRegion label="Loading your learning analytics">
            {/* Lead sentence + the 4 stat columns + suggestion card. */}
            <div style={{ display: 'flex', gap: 36, alignItems: 'flex-start', margin: 'var(--space-5) 0 var(--space-6)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SkeletonText lines={2} lastLineWidth="54%" style={{ marginBottom: 'var(--space-5)' }} />
                <div style={{ display: 'flex', gap: 28 }}>
                  {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} style={{ flex: 1 }}>
                      <Skeleton width="46%" height={22} style={{ marginBottom: 6 }} />
                      <Skeleton width="82%" height={12} />
                    </div>
                  ))}
                </div>
              </div>
              <SkeletonPanel style={{ flex: 'none', width: 320 }}>
                <Skeleton width="44%" height={11} style={{ marginBottom: 'var(--space-3)' }} />
                <Skeleton width="78%" height={15} style={{ marginBottom: 'var(--space-2)' }} />
                <Skeleton height={12} style={{ marginBottom: 'var(--space-4)' }} />
                <Skeleton height={34} />
              </SkeletonPanel>
            </div>

            {/* Two side-by-side chart cards, then the full-width activity card. */}
            <div className="ins-grid-2">
              {Array.from({ length: 2 }, (_, i) => (
                <SkeletonPanel key={i}>
                  <Skeleton width="40%" height={16} style={{ marginBottom: 'var(--space-3)' }} />
                  <Skeleton height={140} />
                </SkeletonPanel>
              ))}
            </div>
            <SkeletonPanel style={{ marginTop: 'var(--space-4)' }}>
              <Skeleton width="28%" height={16} style={{ marginBottom: 'var(--space-3)' }} />
              <Skeleton height={120} />
            </SkeletonPanel>
          </SkeletonRegion>
        </PageContainer>
      </>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16
      }}>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{error}</p>
        <button onClick={refetch} className="ins-btn" style={{ width: 'auto' }}>
          Try again
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <>
      <SEO {...SEO_CONFIG.insights} />

      <PageContainer>
        <PageHeader
          title="Your learning"
          subtitle="Habits, strengths, and the record your work has built so far."
        />
        <TabStrip tabs={INSIGHTS_TABS} />

        <InsightsLead data={data} />

        <div className="ins-grid-2">
          <StrengthsCard data={data} />
          <QuizTrendCard data={data} isVisible={isVisible} />
        </div>

        <StudyActivityCard data={data} isVisible={isVisible} />

        <BadgesCard data={data} />

        <LearningStyleCard data={data} />

        {data.adaptiveV2Enabled && (
          <div id="review-queue">
            <ReviewQueueSection initialCount={data.v2ReviewQueueCount || 0} isVisible={isVisible} />
          </div>
        )}

        <div style={{
          marginTop: 32,
          paddingTop: 16,
          borderTop: '1px solid var(--border-subtle)',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Graspding since {data.memberSince || 'recently'}
          </span>
        </div>
      </PageContainer>
    </>
  );
}
